import {
  useComputeValidModeElement,
  useCurrentArea,
} from "@/modes/mode-data-context";
import { changeMode } from "@/store/mode-slice";
import { selectFlyWalkSceneFilter } from "@/store/modes/flywalk-mode-slice";
import { RootState } from "@/store/store";
import { useAppDispatch, useAppStore } from "@/store/store-hooks";
import { SceneFilter } from "@/types/scene-filter";
import {
  GUID,
  includes,
  PropOptional,
  validatePrimitive,
} from "@faro-lotv/foundation";
import {
  IElement,
  IElementGaussianSplats,
  IElementGenericStream,
  isIElementGaussianSplats,
  isIElementGenericStream,
} from "@faro-lotv/ielement-types";
import { useCallback, useMemo } from "react";
import {
  DEFAULT_INITIAL_STATE,
  InitialStateProps,
  SceneFilterLookAtInitialState,
} from "../mode";
import { selectIsElementViewable } from "../mode-selectors";

/**
 * Analyze the current scene and adjust it so it can work for overview mode
 * Makes sure the active element is one of the valid sessions for overview scene
 *
 * @returns the main object overview mode can render (active cloud, active gaussian splats or active cad)
 */
export function useComputeValidOverviewScene():
  | IElementGenericStream
  | IElementGaussianSplats
  | undefined {
  const { dataSessions3d, dataSessions5d } = useCurrentArea();

  const dispatch = useAppDispatch();
  const { getState } = useAppStore();

  const validSessions = useMemo(
    () => [
      ...dataSessions3d.pointClouds,
      ...dataSessions3d.gaussianSplats,
      ...dataSessions5d,
    ],
    [dataSessions3d, dataSessions5d],
  );

  const validModelGuard = useCallback(
    (el: IElement): el is IElementGenericStream =>
      (isIElementGenericStream(el) || isIElementGaussianSplats(el)) &&
      selectIsElementViewable(el)(getState()),
    [getState],
  );

  const fallback = useCallback(() => {
    dispatch(changeMode("sheet"));
  }, [dispatch]);

  return useComputeValidModeElement(validSessions, validModelGuard, fallback);
}

/** An object to compute and parse a state object to create and restore the overview mode states from deep links */
export const OVERVIEW_MODE_INITIAL_STATE: InitialStateProps<SceneFilterLookAtInitialState> =
  {
    compute(state: RootState, viewContext) {
      const scene = selectFlyWalkSceneFilter(state);
      return {
        ...DEFAULT_INITIAL_STATE.compute(state, viewContext),
        scene,
      };
    },
    parse(appState: RootState, modeState) {
      const scene = includes(Object.values(SceneFilter), modeState.scene)
        ? modeState.scene
        : SceneFilter.PointCloud;
      let lookAtId: GUID | undefined;
      // lookAtId is not defined when coming back from the Draft view, so no error should be
      // logged for this standard case.
      if (validatePrimitive(modeState, "lookAtId", "string", PropOptional)) {
        ({ lookAtId } = modeState);
      }
      return {
        ...DEFAULT_INITIAL_STATE.parse(appState, modeState),
        scene,
        lookAtId,
      };
    },
  };
